/**
 * 교사카드 (모바일 증명) 앞면/뒷면
 *  - 앞면: 사용자 디자인(밝은 파랑, 남색 테두리) + NO.교사번호 · 한글 이름(한 칸씩) · 영문 이름 · 교회 · 클럽(영문 대문자) · 배지 · QR
 *  - 뒷면: “Approved Workmen Are Not Ashamed” + 연도/과정/결과 교육 이력 표
 *  - 캔버스로 그려서 화면 표시와 이미지 저장(PNG)에 같은 그림 사용
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { api, CertData } from '../api';
import { certDate, downloadBlob, spacedKoreanName, todayYmd } from '../common';
import { downloadCertificatesPdf } from '../certificate/Certificate';

export type CardExtra = {
  teacherNameEn: string; clubs: string[]; badges: string[]; qrStatic: string;
  history: Array<{ year: number; course: string; result: string; sub: string }>;
  certificates: CertData[];
};
type CardBase = { keyCode: string; teacherName: string; churchName: string };

const W0 = 943; const H0 = 1377; const S = 2;
const FONT = '"Noto Sans KR","Apple SD Gothic Neo","Malgun Gothic",sans-serif';
const BLUE = '#1a2bf0';

const loadImage = (src: string) => new Promise<HTMLImageElement | null>((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const setFont = (ctx: CanvasRenderingContext2D, size: number, bold = true) => { ctx.font = `${bold ? 700 : 400} ${size * S}px ${FONT}`; };
const center = (ctx: CanvasRenderingContext2D, text: string, y: number, color: string, shadow?: string) => {
  const w = ctx.measureText(text).width;
  const x = (W0 * S - w) / 2;
  if (shadow) { ctx.fillStyle = shadow; ctx.fillText(text, x + 2 * S, y * S + 3 * S); }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y * S);
};
/** 너무 긴 글자는 폭에 맞춰 줄임 */
const fitSize = (ctx: CanvasRenderingContext2D, text: string, size: number, maxW: number, bold = true) => {
  let s = size;
  setFont(ctx, s, bold);
  while (s > 14 && ctx.measureText(text).width > maxW * S) { s -= 2; setFont(ctx, s, bold); }
  return s;
};

export const drawFront = async (canvas: HTMLCanvasElement, base: CardBase, extra: CardExtra | null, qrCanvas: HTMLCanvasElement | null) => {
  canvas.width = W0 * S; canvas.height = H0 * S;
  const ctx = canvas.getContext('2d')!;
  const bg = await loadImage('/bt-card/card-front.jpg');
  if (bg) ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);
  else { ctx.fillStyle = '#3d5bff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.textBaseline = 'top';
  setFont(ctx, 28); center(ctx, `NO. ${base.keyCode}`, 192, 'rgb(225,231,255)');
  const name = spacedKoreanName(base.teacherName);
  fitSize(ctx, name, 80, 780); center(ctx, name, 386, '#fff', 'rgb(20,40,170)');
  if (extra?.teacherNameEn) { fitSize(ctx, extra.teacherNameEn.toUpperCase(), 28, 780); center(ctx, extra.teacherNameEn.toUpperCase(), 494, 'rgb(225,231,255)'); }
  fitSize(ctx, base.churchName || '', 48, 800); center(ctx, base.churchName || '', 552, '#fff', 'rgb(20,40,170)');
  if (extra?.clubs.length) { const t = extra.clubs.join(' · '); fitSize(ctx, t, 28, 800); center(ctx, t, 630, 'rgb(255,226,120)'); }
  // 배지
  const badges = extra?.badges || [];
  if (badges.length) {
    setFont(ctx, 19);
    const ws = badges.map((b) => ctx.measureText(b).width + 30 * S);
    let x = (W0 * S - (ws.reduce((a, b) => a + b, 0) + (ws.length - 1) * 10 * S)) / 2;
    const y = 862 * S;
    badges.forEach((b, i) => {
      ctx.fillStyle = 'rgb(255,214,64)';
      ctx.beginPath();
      (ctx as any).roundRect ? (ctx as any).roundRect(x, y, ws[i], 36 * S, 18 * S) : ctx.rect(x, y, ws[i], 36 * S);
      ctx.fill();
      ctx.fillStyle = 'rgb(59,47,0)';
      ctx.fillText(b, x + 15 * S, y + 6 * S);
      x += ws[i] + 10 * S;
    });
  }
  // QR: 흰 사각형(309~632 × 911~1243)에 꽉 차게
  if (qrCanvas) {
    const bx0 = 309; const by0 = 911; const bx1 = 632; const by1 = 1243; const m = 18;
    const side = Math.min(bx1 - bx0, by1 - by0) - 2 * m;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrCanvas, (bx0 + ((bx1 - bx0) - side) / 2) * S, (by0 + ((by1 - by0) - side) / 2) * S, side * S, side * S);
    ctx.imageSmoothingEnabled = true;
  }
};

export const drawBack = async (canvas: HTMLCanvasElement, base: CardBase, extra: CardExtra | null) => {
  canvas.width = W0 * S; canvas.height = H0 * S;
  const ctx = canvas.getContext('2d')!;
  const bg = await loadImage('/bt-card/card-back.jpg');
  if (bg) ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);
  else { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.textBaseline = 'top';
  const head = `${base.teacherName}  ·  ${base.churchName}  ·  ${base.keyCode}`;
  fitSize(ctx, head, 19, 780, false); center(ctx, head, 268, 'rgb(90,90,90)');
  const x0 = 86; const x1 = W0 - 86; let y = 318;
  const cols = [x0 + 10, x0 + 120, x1 - 170];
  const line = (yy: number, color: string, w: number) => { ctx.strokeStyle = color; ctx.lineWidth = w * S; ctx.beginPath(); ctx.moveTo(x0 * S, yy * S); ctx.lineTo(x1 * S, yy * S); ctx.stroke(); };
  line(y, 'rgb(30,30,30)', 2);
  setFont(ctx, 18); ctx.fillStyle = 'rgb(30,30,30)';
  ['연도', '과정', '결과'].forEach((h, i) => ctx.fillText(h, cols[i] * S, (y + 10) * S));
  y += 48; line(y, 'rgb(120,120,120)', 1);
  const all = extra?.history || [];
  const MAX = 12;
  const rows = all.length > MAX ? all.slice(0, MAX - 1) : all;
  rows.forEach((r) => {
    ctx.fillStyle = 'rgb(25,25,25)';
    setFont(ctx, 19); ctx.fillText(String(r.year), cols[0] * S, (y + 14) * S);
    fitSize(ctx, r.course, 19, cols[2] - cols[1] - 20); ctx.fillText(r.course, cols[1] * S, (y + 12) * S);
    if (r.sub) { setFont(ctx, 15, false); ctx.fillStyle = 'rgb(110,110,110)'; ctx.fillText(r.sub.slice(0, 40), cols[1] * S, (y + 40) * S); }
    setFont(ctx, 19, false); ctx.fillStyle = 'rgb(25,25,25)'; ctx.fillText(r.result, cols[2] * S, (y + 14) * S);
    y += 72; line(y, 'rgb(190,190,190)', 1);
  });
  if (all.length > MAX) { setFont(ctx, 17, false); ctx.fillStyle = 'rgb(110,110,110)'; ctx.fillText(`외 ${all.length - rows.length}건`, cols[1] * S, (y + 14) * S); }
  if (!all.length) { setFont(ctx, 18, false); center(ctx, '아직 교육 이력이 없습니다', y + 30, 'rgb(130,130,130)'); }
  setFont(ctx, 15, false); center(ctx, `Awana Korea · awanaevent.com  |  ${todayYmd()} 기준`, 1306, 'rgb(120,120,120)');
};

// ───────── 단색 아이콘 ─────────
const Icon: React.FC<{ kind: 'attend' | 'edu' | 'cert' }> = ({ kind }) => (
  <svg width="30" height="30" viewBox="0 0 32 32" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {kind === 'attend' && (<>
      <rect x="4" y="4" width="9" height="9" rx="1.5" /><rect x="19" y="4" width="9" height="9" rx="1.5" /><rect x="4" y="19" width="9" height="9" rx="1.5" />
      <path d="M7.5 7.5h2v2h-2zM22.5 7.5h2v2h-2zM7.5 22.5h2v2h-2z" fill={BLUE} stroke="none" />
      <path d="M18.5 23.5l3 3 6-7" />
    </>)}
    {kind === 'edu' && (<>
      <path d="M2.5 12L16 6l13.5 6L16 18z" /><path d="M8 15v6c0 2 3.6 4 8 4s8-2 8-4v-6" /><path d="M29.5 12v8" />
    </>)}
    {kind === 'cert' && (<>
      <rect x="3" y="5" width="22" height="16" rx="2" /><path d="M7 10h10M7 14h7" />
      <circle cx="23" cy="20" r="4.5" /><path d="M20.5 24l-1.5 5 4-2 4 2-1.5-5" />
    </>)}
  </svg>
);

const TeacherCardView: React.FC<{ base: CardBase; liveQrValue: string }> = ({ base, liveQrValue }) => {
  const navigate = useNavigate();
  const [extra, setExtra] = useState<CardExtra | null>(null);
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [certOpen, setCertOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const frontRef = useRef<HTMLCanvasElement>(null);
  const backRef = useRef<HTMLCanvasElement>(null);
  const liveQrId = 'bt-card-qr-live';
  const staticQrId = 'bt-card-qr-static';

  useEffect(() => { api<{ data: CardExtra }>('/teacher-id/card-extra').then((r) => setExtra(r.data)).catch(() => setExtra(null)); }, []);
  useEffect(() => {
    if (frontRef.current) drawFront(frontRef.current, base, extra, document.getElementById(liveQrValue ? liveQrId : staticQrId) as HTMLCanvasElement | null);
    if (backRef.current) drawBack(backRef.current, base, extra);
  }, [base, extra, liveQrValue]);

  const saveImage = async () => {
    setBusy('save');
    try {
      const front = document.createElement('canvas');
      const back = document.createElement('canvas');
      // 저장 이미지에는 만료되지 않는 교사 QR(BT-TEACHER:교사번호)을 넣음
      await drawFront(front, base, extra, document.getElementById(staticQrId) as HTMLCanvasElement | null);
      await drawBack(back, base, extra);
      const gap = 60;
      const out = document.createElement('canvas');
      out.width = front.width * 2 + gap * 3; out.height = front.height + gap * 2;
      const ctx = out.getContext('2d')!;
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(front, gap, gap); ctx.drawImage(back, front.width + gap * 2, gap);
      out.toBlob((blob) => blob && downloadBlob(blob, `교사카드_${base.teacherName}.png`), 'image/png');
    } finally { setBusy(''); }
  };
  const certPdf = async (c: CertData) => {
    setBusy(c._id); setError('');
    try { await downloadCertificatesPdf([c]); } catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };

  const cardW = 'min(360px, 88vw, calc((100dvh - 270px) * 0.685))';
  return (
    <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.5 }}>
      <Box sx={{ display: 'none' }}>
        {liveQrValue && <QRCodeCanvas id={liveQrId} value={liveQrValue} size={574} level="Q" bgColor="#ffffff" fgColor="#000000" />}
        <QRCodeCanvas id={staticQrId} value={extra?.qrStatic || `BT-TEACHER:${base.keyCode}`} size={574} level="Q" />
      </Box>
      <Box onClick={() => setSide(side === 'front' ? 'back' : 'front')} sx={{ width: cardW, aspectRatio: `${W0} / ${H0}`, perspective: '1600px', cursor: 'pointer' }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%', transition: 'transform .6s', transformStyle: 'preserve-3d', transform: side === 'back' ? 'rotateY(180deg)' : 'none' }}>
          <Box component="canvas" ref={frontRef} aria-label="교사카드 앞면" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '18px', backfaceVisibility: 'hidden', boxShadow: '0 12px 30px rgba(20,40,170,.25)' }} />
          <Box component="canvas" ref={backRef} aria-label="교사카드 뒷면" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '18px', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', boxShadow: '0 12px 30px rgba(20,40,170,.25)' }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
        {(['front', 'back'] as const).map((s) => <Box key={s} sx={{ width: side === s ? 18 : 7, height: 7, borderRadius: 4, bgcolor: side === s ? BLUE : '#c7cdf5', transition: 'all .3s' }} />)}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" onClick={() => setSide(side === 'front' ? 'back' : 'front')} sx={{ color: BLUE, borderColor: '#c7cdf5' }}>
          {side === 'front' ? '↻ 뒷면 보기' : '↻ 앞면 보기'}
        </Button>
        <Button size="small" variant="outlined" disabled={busy === 'save'} onClick={saveImage} sx={{ color: BLUE, borderColor: '#c7cdf5' }}>⬇ 이미지로 저장</Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, width: cardW }}>
        {[
          { kind: 'attend' as const, label: '출석하기', onClick: () => navigate('/go?from=card') },
          { kind: 'edu' as const, label: '내 교육 이력', onClick: () => setSide('back') },
          { kind: 'cert' as const, label: '내 수료증', onClick: () => setCertOpen(true) },
        ].map((b) => (
          <Box key={b.label} component="button" onClick={b.onClick}
            sx={{ border: '1px solid #dfe3fb', bgcolor: '#fff', borderRadius: 3, py: 1.25, display: 'grid', justifyItems: 'center', gap: 0.5, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { bgcolor: '#f3f5ff' } }}>
            <Icon kind={b.kind} />
            <Typography fontSize={13} fontWeight={700} color={BLUE}>{b.label}</Typography>
          </Box>
        ))}
      </Box>
      <Dialog open={certOpen} onClose={() => setCertOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><Box flex={1} fontWeight={800}>내 수료증</Box><IconButton onClick={() => setCertOpen(false)}><Close /></IconButton></DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {!extra?.certificates.length ? <Typography color="text.secondary">발급된 수료증이 없습니다. 교회에서 완성보고서를 제출하면 본부 확인 후 발급됩니다.</Typography>
            : extra.certificates.map((c) => (
              <Box key={c._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid #eee' }}>
                <Box flex={1}>
                  <Typography fontWeight={800}>BT · {c.clubLabel}</Typography>
                  <Typography fontSize={13} color="text.secondary">No. {c.serial} · {certDate(c.completedAt)}</Typography>
                </Box>
                <Button size="small" variant="outlined" disabled={busy === c._id} onClick={() => certPdf(c)}>{busy === c._id ? '…' : 'PDF'}</Button>
              </Box>
            ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TeacherCardView;
